<template>
  <div class="tree-management-container">
    <div class="header">
      <h1>📂 工作组管理系统</h1>
      <el-button 
        type="primary" 
        icon="el-icon-plus" 
        @click="showCreateGroupDialog">
        新建工作组
      </el-button>
    </div>
    
    <!-- 左右分栏布局 -->
    <div class="main-content">
      <!-- 左侧：树形结构 -->
      <div class="left-panel">
        <div class="tree-container" v-loading="loading">
          <el-tree
            ref="treeRef"
            v-if="treeData.length > 0"
            :data="treeData"
            node-key="id"
            default-expand-all
            highlight-current
            :expand-on-click-node="false">
        <span class="custom-tree-node" slot-scope="{ node, data }">
          <span class="tree-node-label">
            <i class="tree-node-icon" :class="data.isGroup ? 'el-icon-folder-opened' : 'el-icon-document'"></i>
            <span>{{ data.label }}</span>
          </span>
          <span class="tree-node-actions">
            <!-- 工作组下拉菜单 -->
            <el-dropdown v-if="data.isGroup" trigger="click" @command="handleCommand">
              <el-button size="mini" icon="el-icon-more"></el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :command="{action: 'createDatabase', data: data}" icon="el-icon-plus">
                  新建数据集
                </el-dropdown-item>
                <el-dropdown-item :command="{action: 'manageMembers', data: data}" icon="el-icon-user">
                  管理成员
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            
            <!-- 数据集下拉菜单 -->
            <el-dropdown v-if="!data.isGroup" trigger="click" @command="handleCommand">
              <el-button size="mini" icon="el-icon-more"></el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :command="{action: 'viewDataset', data: data}" icon="el-icon-view">
                  查看详情
                </el-dropdown-item>
                <el-dropdown-item :command="{action: 'shareDataset', data: data}" icon="el-icon-share">
                  分享链接
                </el-dropdown-item>
                <el-dropdown-item :command="{action: 'updateDatabase', data: data}" icon="el-icon-edit" divided>
                  修改名称
                </el-dropdown-item>
                <el-dropdown-item :command="{action: 'transferOwner', data: data}" icon="el-icon-s-custom">
                  权限转移
                </el-dropdown-item>
                <el-dropdown-item :command="{action: 'deleteDatabase', data: data}" icon="el-icon-delete" divided>
                  删除
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </span>
        </span>
          </el-tree>
          
          <div v-else class="empty-state">
            <i class="el-icon-folder-opened"></i>
            <p>暂无工作组，请先创建工作组</p>
          </div>
        </div>
      </div>
      
      <!-- 右侧：内容显示区 -->
      <div class="right-panel">
        <!-- 未选择数据集时的提示 -->
        <div v-if="!currentEditDatabaseId" class="empty-content">
          <i class="el-icon-document"></i>
          <p>请选择左侧数据集查看内容</p>
        </div>
        
        <!-- 表格编辑器（非弹框模式） -->
        <dataset-table-editor
          ref="datasetTableEditor"
          v-if="currentEditDatabaseId && !currentEditIsTxt"
          :key="editorKey"
          :visible="true"
          :database-id="currentEditDatabaseId"
          :dataset-name="currentEditDatasetName"
          :user-operator-id="USER_OPERATOR_ID"
          :is-panel-mode="true"
          @close="handleCloseEditor">
        </dataset-table-editor>
        
        <!-- 文本编辑器（非弹框模式） -->
        <dataset-text-editor
          ref="datasetTextEditor"
          v-if="currentEditDatabaseId && currentEditIsTxt"
          :key="editorKey"
          :visible="true"
          :database-id="currentEditDatabaseId"
          :dataset-name="currentEditDatasetName"
          :user-operator-id="USER_OPERATOR_ID"
          :is-panel-mode="true"
          @close="handleCloseEditor">
        </dataset-text-editor>
      </div>
    </div>
    
    <!-- 新建工作组对话框 -->
    <el-dialog title="新建工作组" :visible.sync="createGroupDialogVisible" width="500px">
      <el-form :model="groupForm" :rules="groupRules" ref="groupForm" label-width="100px">
        <el-form-item label="工作组名称" prop="group_name">
          <el-input v-model="groupForm.group_name" placeholder="请输入工作组名称"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="createGroupDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitCreateGroup">确 定</el-button>
      </span>
    </el-dialog>
    
    <!-- 新建数据集对话框 -->
    <el-dialog title="新建数据集" :visible.sync="createDatabaseDialogVisible" width="500px">
      <el-form :model="databaseForm" :rules="databaseRules" ref="databaseForm" label-width="100px">
        <el-form-item label="数据集名称" prop="database_name">
          <el-input v-model="databaseForm.database_name" placeholder="请输入数据集名称"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="createDatabaseDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitCreateDatabase">确 定</el-button>
      </span>
    </el-dialog>
    
    <!-- 修改数据集名称对话框 -->
    <el-dialog title="修改数据集名称" :visible.sync="updateDatabaseDialogVisible" width="500px">
      <el-form :model="updateDatabaseForm" :rules="databaseRules" ref="updateDatabaseForm" label-width="100px">
        <el-form-item label="数据集名称" prop="database_name">
          <el-input v-model="updateDatabaseForm.database_name" placeholder="请输入数据集名称"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="updateDatabaseDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitUpdateDatabase">确 定</el-button>
      </span>
    </el-dialog>
    
    <!-- 成员管理对话框 -->
    <member-selector
      :visible.sync="memberSelectorVisible"
      :group-id="currentManageGroupId"
      :is-group-owner="currentGroupIsOwner"
      @members-updated="handleMembersUpdated">
    </member-selector>
    
    <!-- 权限转移对话框 -->
    <el-dialog
      title="权限转移"
      :visible.sync="transferOwnerDialogVisible"
      width="500px"
      @close="handleTransferDialogClose">
      <div v-loading="transferLoading">
        <el-alert
          title="提示"
          type="warning"
          :closable="false"
          style="margin-bottom: 20px;">
          <div slot="title">
            <p>权限转移后，您将失去该数据集的管理权限</p>
            <p>请选择要转移给的成员：</p>
          </div>
        </el-alert>
        
        <el-select
          v-model="selectedTransferUserId"
          placeholder="请选择成员"
          style="width: 100%;"
          filterable>
          <el-option
            v-for="user in transferUserList"
            :key="user.id"
            :label="`${user.userName} (${user.userAccount})`"
            :value="user.id">
            <span style="float: left">{{ user.userName }}</span>
            <span style="float: right; color: #8492a6; font-size: 13px">{{ user.userAccount }}</span>
          </el-option>
        </el-select>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="transferOwnerDialogVisible = false">取 消</el-button>
        <el-button 
          type="primary" 
          @click="submitTransferOwner"
          :disabled="!selectedTransferUserId">
          确认转移
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
// 引入封装好的 async API 函数
import {
  createGroup,
  getGroupList,
  createDatabase,
  getShareDatabaseList,
  updateDatabaseName,
  deleteDatabase,
  initDemoUsers,
  getUserIdByUserOperatorId,
  getTransferDatabaseUserList,
  transferDatabaseOwner
} from '@/api'

// 引入成员选择器组件
import MemberSelector from './MemberSelector.vue'
// 引入数据集编辑器组件
import DatasetTableEditor from './DatasetTableEditor.vue'
import DatasetTextEditor from './DatasetTextEditor.vue'

// 当前用户ID（实际项目中应该从登录状态获取）
const USER_OPERATOR_ID = 'user_001'

export default {
  name: 'TreeNodeManagement',
  components: {
    MemberSelector,
    DatasetTableEditor,
    DatasetTextEditor
  },
  data() {
    return {
      loading: false,
      treeData: [],
      userId: null, // 当前登录用户的userId
      
      // 新建工作组
      createGroupDialogVisible: false,
      groupForm: {
        group_name: ''
      },
      groupRules: {
        group_name: [
          { required: true, message: '请输入工作组名称', trigger: 'blur' },
          { min: 1, max: 50, message: '长度在 1 到 50 个字符', trigger: 'blur' }
        ]
      },
      
      // 新建数据集
      createDatabaseDialogVisible: false,
      currentGroupId: null,
      databaseForm: {
        database_name: ''
      },
      databaseRules: {
        database_name: [
          { required: true, message: '请输入数据集名称', trigger: 'blur' },
          { min: 1, max: 50, message: '长度在 1 到 50 个字符', trigger: 'blur' }
        ]
      },
      
      // 修改数据集
      updateDatabaseDialogVisible: false,
      currentDatabaseId: null,
      updateDatabaseForm: {
        database_name: ''
      },
      
      // 成员管理
      memberSelectorVisible: false,
      currentManageGroupId: null,
      currentGroupIsOwner: false,
      
      // 数据集编辑器
      currentEditDatabaseId: null,
      currentEditDatasetName: '',
      currentEditIsTxt: false,
      editorKey: 0, // 用于强制刷新编辑器
      USER_OPERATOR_ID: USER_OPERATOR_ID,
      
      // 权限转移
      transferOwnerDialogVisible: false,
      transferLoading: false,
      transferUserList: [],
      selectedTransferUserId: null,
      currentTransferDatabaseId: null,
      currentTransferGroupId: null
    }
  },
  
  async created() {
    // 先初始化演示数据
    await this.initDemoData()
    // 获取当前用户的userId
    await this.fetchUserId()
    // 加载树数据
    await this.loadTreeData()
    // 检查路由参数，如果有 shareCode 则自动加载对应数据集
    await this.checkRouteAndLoadDataset()
  },
  
  watch: {
    // 监听路由变化（shareCode）
    '$route.params.shareCode': {
      handler(newShareCode) {
        if (newShareCode) {
          this.loadDatasetFromShareCode(newShareCode)
        } else {
          // 回到首页，清空右侧面板
          this.handleCloseEditor()
        }
      }
    }
  },
  
  methods: {
    /**
     * 初始化演示数据
     */
    async initDemoData() {
      try {
        await initDemoUsers()
      } catch (error) {
        console.error('初始化演示数据失败:', error)
      }
    },
    
    /**
     * 检查路由并加载数据集
     */
    async checkRouteAndLoadDataset() {
      const shareCode = this.$route.params.shareCode
      if (shareCode) {
        await this.loadDatasetFromShareCode(shareCode)
      }
    },
    
    /**
     * 从分享码加载数据集
     */
    async loadDatasetFromShareCode(shareCode) {
      try {
        // 先从树数据中查找（如果已有数据）
        let foundDataset = null
        for (const group of this.treeData) {
          const dataset = group.children.find(
            child => child.shareCode === shareCode
          )
          if (dataset) {
            foundDataset = dataset
            break
          }
        }
        
        if (foundDataset) {
          // 从本地树数据找到了
          this.currentEditDatabaseId = foundDataset.databaseId
          this.currentEditDatasetName = foundDataset.label
          
          // 获取数据类型（需传入 user_id 验证权限）
          const { getDataDetailById } = await import('@/api')
          const response = await getDataDetailById({
            share_code: shareCode,
            user_id: USER_OPERATOR_ID
          })
          
          if (response.code === 200 && response.data) {
            this.currentEditIsTxt = response.data.isTxt === 1
          }
          
          // 设置树节点高亮
          this.$nextTick(() => {
            const treeRef = this.$refs.treeRef
            if (treeRef) {
              treeRef.setCurrentKey(`database_${foundDataset.databaseId}`)
            }
          })
        } else {
          // 本地没有，通过 API 查询（需传入 user_id 验证权限）
          const { getDatasetByShareCode, getDataDetailById } = await import('@/api')
          const infoRes = await getDatasetByShareCode(shareCode, USER_OPERATOR_ID)
          
          if (infoRes.code === 200 && infoRes.data) {
            this.currentEditDatabaseId = infoRes.data.databaseId
            this.currentEditDatasetName = infoRes.data.databaseName
            
            // 获取数据类型
            const response = await getDataDetailById({
              share_code: shareCode,
              user_id: USER_OPERATOR_ID
            })
            
            if (response.code === 200 && response.data) {
              this.currentEditIsTxt = response.data.isTxt === 1
            }
          } else if (infoRes.code === 403) {
            // 无权限访问
            this.$message.warning('您不是该数据集的共享成员，无权访问')
            this.$router.replace({ name: 'home' })
          } else {
            // 数据集不存在，提示并重定向到首页
            this.$message.warning('数据集不存在或链接已失效')
            this.$router.replace({ name: 'home' })
          }
        }
      } catch (error) {
        console.error('加载数据集失败:', error)
        this.$message.error('加载数据集失败')
        this.$router.replace({ name: 'home' })
      }
    },
    
    /**
     * 获取当前用户的userId
     */
    async fetchUserId() {
      try {
        const response = await getUserIdByUserOperatorId(USER_OPERATOR_ID)
        if (response.data && response.data.userId) {
          this.userId = response.data.userId
          console.log('获取userId成功:', this.userId)
        } else {
          console.error('获取userId失败：返回数据为空')
          this.$message.error('获取用户信息失败，请刷新页面重试')
        }
      } catch (error) {
        console.error('获取userId失败:', error)
        this.$message.error('获取用户信息失败，请刷新页面重试')
      }
    },
    
    /**
     * 加载树形数据 - 使用封装的 async 函数
     */
    async loadTreeData() {
      this.loading = true
      try {
        // 1. 调用 async 函数获取工作组列表
        const groupRes = await getGroupList({
          user_operator_id: USER_OPERATOR_ID
        })
        
        const groupList = groupRes.data.groupList
        const treeData = []
        
        // 2. 为每个工作组获取数据集列表
        for (const group of groupList) {
          // 构建工作组节点
          const groupNode = {
            id: `group_${group.id}`,
            label: group.groupName,
            groupId: group.id,
            isGroup: true,
            isGroupOwner: group.owner === 1,  // 是否是工作组创建者
            children: []
          }
          
          try {
            // 3. 调用 async 函数获取数据集列表
            const databaseRes = await getShareDatabaseList({
              group_id: group.id
            })
            
            const databaseList = databaseRes.data.shareDatabaseList
            
            // 4. 利用 groupId 将数据集挂载到 children 数组
            databaseList.forEach(database => {
              groupNode.children.push({
                id: `database_${database.id}`,
                label: database.databaseName,
                databaseId: database.id,
                groupId: database.groupId, // 关键：保留 groupId
                shareCode: database.shareCode, // 分享码
                isGroup: false
              })
            })
          } catch (error) {
            console.error(`获取工作组 ${group.id} 的数据集失败:`, error)
          }
          
          treeData.push(groupNode)
        }
        
        this.treeData = treeData
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败，请检查后端服务是否启动')
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 显示新建工作组对话框
     */
    showCreateGroupDialog() {
      this.groupForm.group_name = ''
      this.createGroupDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.groupForm) {
          this.$refs.groupForm.clearValidate()
        }
      })
    },
    
    /**
     * 提交创建工作组 - 使用封装的 async 函数
     */
    submitCreateGroup() {
      this.$refs.groupForm.validate(async (valid) => {
        if (!valid) return
        
        try {
          // 调用 async 函数创建工作组
          await createGroup({
            user_operator_id: USER_OPERATOR_ID,
            group_name: this.groupForm.group_name
          })
          
          this.$message.success('创建成功')
          this.createGroupDialogVisible = false
          this.loadTreeData() // 重新加载数据
        } catch (error) {
          console.error('创建工作组失败:', error)
          this.$message.error(error.message || '创建失败')
        }
      })
    },
    
    /**
     * 处理新建数据集
     */
    handleCreateDatabase(groupData) {
      this.currentGroupId = groupData.groupId
      this.databaseForm.database_name = ''
      this.createDatabaseDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.databaseForm) {
          this.$refs.databaseForm.clearValidate()
        }
      })
    },
    
    /**
     * 提交创建数据集 - 使用封装的 async 函数
     */
    submitCreateDatabase() {
      this.$refs.databaseForm.validate(async (valid) => {
        if (!valid) return
        
        try {
          // 调用 async 函数创建数据集
          await createDatabase({
            group_id: this.currentGroupId,
            user_operator_id: USER_OPERATOR_ID,
            database_name: this.databaseForm.database_name
          })
          
          this.$message.success('创建成功')
          this.createDatabaseDialogVisible = false
          this.loadTreeData() // 重新加载数据
        } catch (error) {
          console.error('创建数据集失败:', error)
          this.$message.error(error.message || '创建失败')
        }
      })
    },
    
    /**
     * 处理修改数据集名称
     */
    handleUpdateDatabase(databaseData) {
      this.currentDatabaseId = databaseData.databaseId
      this.updateDatabaseForm.database_name = databaseData.label
      this.updateDatabaseDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.updateDatabaseForm) {
          this.$refs.updateDatabaseForm.clearValidate()
        }
      })
    },
    
    /**
     * 提交修改数据集名称 - 使用封装的 async 函数
     */
    submitUpdateDatabase() {
      this.$refs.updateDatabaseForm.validate(async (valid) => {
        if (!valid) return
        
        try {
          // 调用 async 函数修改数据集名称
          await updateDatabaseName({
            share_database_id: this.currentDatabaseId,
            database_name: this.updateDatabaseForm.database_name
          })
          
          this.$message.success('修改成功')
          this.updateDatabaseDialogVisible = false
          this.loadTreeData() // 重新加载数据
        } catch (error) {
          console.error('修改数据集名称失败:', error)
          this.$message.error(error.message || '修改失败')
        }
      })
    },
    
    /**
     * 处理删除数据集 - 使用封装的 async 函数
     */
    handleDeleteDatabase(databaseData) {
      this.$confirm(`确定要删除数据集"${databaseData.label}"吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          // 调用 async 函数删除数据集
          await deleteDatabase({
            share_database_id: databaseData.databaseId
          })
          
          this.$message.success('删除成功')
          this.loadTreeData() // 重新加载数据
        } catch (error) {
          console.error('删除数据集失败:', error)
          this.$message.error(error.message || '删除失败')
        }
      }).catch(() => {
        // 取消删除
      })
    },
    
    /**
     * 处理管理成员
     */
    handleManageMembers(groupData) {
      this.currentManageGroupId = groupData.groupId
      this.currentGroupIsOwner = groupData.isGroupOwner || false
      this.memberSelectorVisible = true
    },
    
    /**
     * 成员更新后的回调
     */
    handleMembersUpdated() {
      console.log('成员已更新')
      // 如果当前正在查看某个数据集，刷新右侧面板
      // 因为成员变化可能影响权限显示
      if (this.currentEditDatabaseId) {
        this.refreshDatasetEditor()
      }
    },
    
    /**
     * 统一处理下拉菜单命令
     */
    handleCommand(command) {
      const { action, data } = command
      
      switch (action) {
        case 'createDatabase':
          this.handleCreateDatabase(data)
          break
        case 'manageMembers':
          this.handleManageMembers(data)
          break
        case 'viewDataset':
          this.handleViewDataset(data)
          break
        case 'shareDataset':
          this.handleShareDataset(data)
          break
        case 'updateDatabase':
          this.handleUpdateDatabase(data)
          break
        case 'transferOwner':
          this.handleTransferOwner(data)
          break
        case 'deleteDatabase':
          this.handleDeleteDatabase(data)
          break
        default:
          console.log('未知操作:', action)
      }
    },
    
    /**
     * 查看数据集详情
     */
    async handleViewDataset(data) {
      try {
        // 更新路由（使用 shareCode）
        const currentShareCode = this.$route.params.shareCode
        if (data.shareCode && currentShareCode !== data.shareCode) {
          this.$router.push({
            name: 'share',
            params: { shareCode: data.shareCode }
          })
        }
        
        this.currentEditDatabaseId = data.databaseId
        this.currentEditDatasetName = data.label
        
        // 先加载数据判断类型（需传入 user_id 验证权限）
        const { getDataDetailById } = await import('@/api')
        const response = await getDataDetailById({
          database_id: data.databaseId,
          user_id: USER_OPERATOR_ID
        })
        
        if (response.code === 200 && response.data) {
          // 设置是否为文本类型
          this.currentEditIsTxt = response.data.isTxt === 1
        }
      } catch (error) {
        console.error('打开数据集失败:', error)
        this.$message.error(error.message || '打开数据集失败')
      }
    },
    
    /**
     * 分享数据集链接
     */
    handleShareDataset(data) {
      if (!data.shareCode) {
        this.$message.warning('该数据集暂无分享码，请刷新页面重试')
        return
      }
      
      const url = `${window.location.origin}${window.location.pathname}#/s/${data.shareCode}`
      
      // 复制到剪贴板
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          this.$message.success('链接已复制到剪贴板')
        }).catch(() => {
          this.fallbackCopyToClipboard(url)
        })
      } else {
        this.fallbackCopyToClipboard(url)
      }
    },
    
    /**
     * 降级复制方法（兼容旧浏览器）
     */
    fallbackCopyToClipboard(text) {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-9999px'
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        this.$message.success('链接已复制到剪贴板')
      } catch (err) {
        this.$message.error('复制失败，请手动复制')
        console.error('复制失败:', err)
      }
      document.body.removeChild(textArea)
    },
    
    /**
     * 关闭编辑器
     */
    handleCloseEditor() {
      this.currentEditDatabaseId = null
      this.currentEditDatasetName = ''
      this.currentEditIsTxt = false
      
      // 更新路由到首页（如果当前不在首页）
      if (this.$route.name !== 'home' && this.$route.name !== undefined) {
        this.$router.push({ name: 'home' })
      }
      
      // 清除树节点高亮
      this.$nextTick(() => {
        const treeRef = this.$refs.treeRef
        if (treeRef) {
          treeRef.setCurrentKey(null)
        }
      })
    },
    
    /**
     * 处理权限转移
     */
    async handleTransferOwner(data) {
      try {
        this.currentTransferDatabaseId = data.databaseId
        this.currentTransferGroupId = data.groupId
        this.transferOwnerDialogVisible = true
        
        // 加载可转移的成员列表
        this.transferLoading = true
        const response = await getTransferDatabaseUserList({
          database_id: data.databaseId,
          group_id: data.groupId
        })
        
        if (response.data && response.data.userList) {
          this.transferUserList = response.data.userList
          
          if (this.transferUserList.length === 0) {
            this.$message.warning('该数据集没有可转移的成员，请先添加共享成员')
            this.transferOwnerDialogVisible = false
          }
        }
      } catch (error) {
        console.error('加载转移成员列表失败:', error)
        this.$message.error('加载成员列表失败')
        this.transferOwnerDialogVisible = false
      } finally {
        this.transferLoading = false
      }
    },
    
    /**
     * 提交权限转移
     */
    async submitTransferOwner() {
      if (!this.selectedTransferUserId) {
        this.$message.warning('请选择要转移给的成员')
        return
      }
      
      try {
        await this.$confirm('确定要转移数据集管理权限吗？转移后您将失去管理权限。', '确认转移', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        this.transferLoading = true
        
        await transferDatabaseOwner({
          share_database_id: this.currentTransferDatabaseId,
          user_id: this.selectedTransferUserId
        })
        
        this.$message.success('权限转移成功！')
        this.transferOwnerDialogVisible = false
        
        // 刷新树数据
        await this.loadTreeData()
        
        // 如果当前正在查看该数据集，需要刷新右侧面板
        if (this.currentEditDatabaseId === this.currentTransferDatabaseId) {
          this.refreshDatasetEditor()
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('权限转移失败:', error)
          this.$message.error('权限转移失败')
        }
      } finally {
        this.transferLoading = false
      }
    },
    
    /**
     * 关闭权限转移对话框时的处理
     */
    handleTransferDialogClose() {
      this.selectedTransferUserId = null
      this.transferUserList = []
      this.currentTransferDatabaseId = null
      this.currentTransferGroupId = null
    },
    
    /**
     * 刷新右侧数据集编辑器（解决问题3）
     * 通过改变 key 强制组件重新渲染
     */
    refreshDatasetEditor() {
      // 改变 editorKey 会强制 Vue 销毁旧组件并创建新组件
      // 从而触发 mounted 和重新加载数据
      this.editorKey++
      console.log('编辑器已刷新，新key:', this.editorKey)
    }
  }
}
</script>

<style scoped>
.tree-management-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: white;
}

.header {
  padding: 20px 30px;
  border-bottom: 2px solid #409EFF;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header h1 {
  margin: 0;
  color: #303133;
  font-size: 24px;
}

/* 主内容区：左右分栏 */
.main-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

/* 左侧面板 */
.left-panel {
  width: 350px;
  flex-shrink: 0;
  border-right: 1px solid #EBEEF5;
  overflow-y: auto;
  background: #FAFAFA;
}

.tree-container {
  padding: 20px;
}

/* 右侧面板 */
.right-panel {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: white;
}

/* 空状态提示 */
.empty-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
}

.empty-content i {
  font-size: 80px;
  margin-bottom: 20px;
  color: #C0C4CC;
}

.empty-content p {
  font-size: 16px;
  margin: 0;
}

/* Element UI 树节点样式覆盖 */
.tree-container >>> .el-tree-node__content {
  height: auto;
  min-height: 36px;
  padding: 6px 0;
}

.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding: 4px 8px 4px 0;
  min-height: 32px;
  width: 100%;
}

.tree-node-label {
  display: flex;
  align-items: center;
  flex: 1;
  margin-right: 15px;
}

.tree-node-icon {
  margin-right: 8px;
  font-size: 16px;
}

.tree-node-actions {
  display: flex;
  gap: 10px;
  margin-left: auto;
  flex-shrink: 0;
  align-items: center;
}

.tree-node-actions .el-dropdown {
  line-height: 1;
}

.tree-node-actions .el-button {
  margin: 0;
  padding: 7px 10px;
}

.empty-state {
  text-align: center;
  padding: 60px 0;
  color: #909399;
}

.empty-state i {
  font-size: 80px;
  margin-bottom: 20px;
  display: block;
}
</style>

